'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';

const STORAGE_KEY = 'outils-spin-session';

interface PersistedSession {
  sessionStartMs: number;
  pausedAtMs: number | null;
  totalPausedMs: number;
}

function loadPersisted(): PersistedSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PersistedSession;
    if (typeof data.sessionStartMs !== 'number' || typeof data.totalPausedMs !== 'number') return null;
    return {
      sessionStartMs: data.sessionStartMs,
      pausedAtMs: data.pausedAtMs ?? null,
      totalPausedMs: data.totalPausedMs,
    };
  } catch {
    return null;
  }
}

function savePersisted(data: PersistedSession | null) {
  if (typeof window === 'undefined') return;
  try {
    if (data === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  } catch {
    // ignore
  }
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

export interface OutilsSpinSessionContextValue {
  sessionStarted: boolean;
  elapsedMs: number;
  formattedElapsed: string;
  isPaused: boolean;
  sessionStartMs: number | null;
  pausedAtMs: number | null;
  totalPausedMs: number;
  startSession: () => void;
  togglePause: () => void;
  endSession: () => void;
}

const OutilsSpinSessionContext = createContext<OutilsSpinSessionContextValue | undefined>(undefined);

export function useOutilsSpinSession() {
  const ctx = useContext(OutilsSpinSessionContext);
  return ctx;
}

export function OutilsSpinSessionProvider({ children }: { children: ReactNode }) {
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionStartMs, setSessionStartMs] = useState<number | null>(null);
  const [pausedAtMs, setPausedAtMs] = useState<number | null>(null);
  const [totalPausedMs, setTotalPausedMs] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const isPaused = pausedAtMs !== null;

  useEffect(() => {
    const persisted = loadPersisted();
    if (persisted) {
      setSessionStarted(true);
      setSessionStartMs(persisted.sessionStartMs);
      setPausedAtMs(persisted.pausedAtMs);
      setTotalPausedMs(persisted.totalPausedMs);
      const elapsed = persisted.pausedAtMs != null
        ? persisted.pausedAtMs - persisted.sessionStartMs - persisted.totalPausedMs
        : Date.now() - persisted.sessionStartMs - persisted.totalPausedMs;
      setElapsedMs(Math.max(0, elapsed));
    }
  }, []);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!sessionStarted || sessionStartMs === null || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (sessionStartMs !== null && isPaused && pausedAtMs !== null) {
        setElapsedMs(pausedAtMs - sessionStartMs - totalPausedMs);
      }
      return;
    }
    const tick = () => {
      setElapsedMs(Date.now() - sessionStartMs - totalPausedMs);
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [sessionStarted, sessionStartMs, totalPausedMs, isPaused, pausedAtMs]);

  const startSession = useCallback(() => {
    const now = Date.now();
    setSessionStarted(true);
    setSessionStartMs(now);
    setPausedAtMs(null);
    setTotalPausedMs(0);
    setElapsedMs(0);
    savePersisted({ sessionStartMs: now, pausedAtMs: null, totalPausedMs: 0 });
  }, []);

  const togglePause = useCallback(() => {
    if (sessionStartMs === null) return;
    if (isPaused) {
      const added = Date.now() - pausedAtMs!;
      const nextTotal = totalPausedMs + added;
      setTotalPausedMs(nextTotal);
      setPausedAtMs(null);
      savePersisted({ sessionStartMs, pausedAtMs: null, totalPausedMs: nextTotal });
    } else {
      const now = Date.now();
      setPausedAtMs(now);
      savePersisted({ sessionStartMs, pausedAtMs: now, totalPausedMs });
    }
  }, [sessionStartMs, isPaused, pausedAtMs, totalPausedMs]);

  const endSession = useCallback(() => {
    setSessionStarted(false);
    setSessionStartMs(null);
    setPausedAtMs(null);
    setTotalPausedMs(0);
    setElapsedMs(0);
    savePersisted(null);
  }, []);

  const displayElapsedMs = isPaused && pausedAtMs !== null && sessionStartMs !== null
    ? pausedAtMs - sessionStartMs - totalPausedMs
    : elapsedMs;

  const value: OutilsSpinSessionContextValue = {
    sessionStarted,
    elapsedMs: displayElapsedMs,
    formattedElapsed: formatDuration(displayElapsedMs),
    isPaused,
    sessionStartMs,
    pausedAtMs,
    totalPausedMs,
    startSession,
    togglePause,
    endSession,
  };

  return (
    <OutilsSpinSessionContext.Provider value={value}>
      {children}
    </OutilsSpinSessionContext.Provider>
  );
}
